import { z } from 'zod';
import { idSchema, idempotencyKeySchema } from './common.js';
import { vehicleTypeSchema } from './vehicle.js';
const phoneE164Schema = z
    .string()
    .trim()
    .regex(/^\+?[1-9]\d{6,14}$/, 'Phone must be E.164 (e.g. +447700900123)')
    .max(16);
const isoCountrySchema = z
    .string()
    .trim()
    .toUpperCase()
    .pipe(z.string().regex(/^[A-Z]{2}$/, 'country must be ISO-2 (e.g. GB)'));
const latitudeSchema = z.number().min(-90).max(90);
const longitudeSchema = z.number().min(-180).max(180);
// S79 three-layer model: paperwork + placement only (motion lives on stops).
export const loadStatusValues = [
    'requested',
    'draft',
    'confirmed',
    'assigned',
    'completed',
    'invoiced',
    'cancelled',
    'failed',
];
export const loadStatusSchema = z.enum(loadStatusValues);
export const loadStopTypeSchema = z.enum(['pickup', 'dropoff']);
export const loadStopStatusSchema = z.enum([
    'pending',
    'en_route',
    'arrived',
    'completed',
    'skipped',
    'failed',
]);
// -------- Path params --------
export const fleetLoadParamsSchema = z.object({
    fleetId: idSchema,
    loadId: idSchema,
});
export const fleetLoadStopParamsSchema = z.object({
    fleetId: idSchema,
    loadId: idSchema,
    stopId: idSchema,
});
// -------- Loads --------
export const listLoadsQuerySchema = z.object({
    status: loadStatusSchema.optional(),
    /**
     * Status-family shorthand (V2-U2, driver History): `done` maps to
     * completed + invoiced server-side, so the app never hardcodes which
     * statuses count as finished.
     */
    family: z.enum(['done']).optional(),
    /** Inclusive completion-time range; only meaningful with family=done. */
    completedFrom: z.string().datetime().optional(),
    completedTo: z.string().datetime().optional(),
    driverUserId: idSchema.optional(),
    customerId: idSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const createLoadBodySchema = z.object({
    /**
     * Optional — service generates `BKR-YYYY-NNNNN` if omitted.
     * If supplied, must be unique within fleet.
     */
    reference: z.string().trim().min(1).max(40).optional(),
    customerId: idSchema.nullable().optional(),
    customerReference: z.string().trim().max(80).nullable().optional(),
    scheduledStartAt: z.string().datetime().nullable().optional(),
    scheduledEndAt: z.string().datetime().nullable().optional(),
    notesInternal: z.string().trim().max(4000).nullable().optional(),
    notesDriver: z.string().trim().max(4000).nullable().optional(),
    notesCustomer: z.string().trim().max(4000).nullable().optional(),
    vehicleType: vehicleTypeSchema.nullable().optional(),
    tailLift: z.boolean().optional(),
    serviceChargePence: z.number().int().min(0).optional(),
    extrasPence: z.number().int().min(0).optional(),
    vatPence: z.number().int().min(0).optional(),
    totalPence: z.number().int().min(0).optional(),
});
export const updateLoadBodySchema = z
    .object({
    reference: z.string().trim().min(1).max(40).optional(),
    customerId: idSchema.nullable().optional(),
    customerReference: z.string().trim().max(80).nullable().optional(),
    scheduledStartAt: z.string().datetime().nullable().optional(),
    scheduledEndAt: z.string().datetime().nullable().optional(),
    notesInternal: z.string().trim().max(4000).nullable().optional(),
    notesDriver: z.string().trim().max(4000).nullable().optional(),
    notesCustomer: z.string().trim().max(4000).nullable().optional(),
    vehicleType: vehicleTypeSchema.nullable().optional(),
    tailLift: z.boolean().optional(),
    serviceChargePence: z.number().int().min(0).optional(),
    extrasPence: z.number().int().min(0).optional(),
    vatPence: z.number().int().min(0).optional(),
    totalPence: z.number().int().min(0).optional(),
})
    .strict();
// -------- Transition / assign / cancel --------
export const transitionBodySchema = z.object({
    to: loadStatusSchema,
    /**
     * Required when:
     *  - `to: "failed"` (any caller)
     *  - `override: true` (any backward / non-pair-allowed move)
     * Otherwise optional but capped.
     */
    reason: z.string().trim().min(5).max(240).optional(),
    /**
     * If true, bypasses the (from, to) pair check. Dispatcher/owner only —
     * route layer enforces the role, service layer enforces reason length.
     */
    override: z.boolean().optional(),
});
export const assignBodySchema = z
    .object({
    driverUserId: idSchema.nullable().optional(),
    vehicleId: idSchema.nullable().optional(),
})
    .strict()
    .refine((v) => v.driverUserId !== undefined || v.vehicleId !== undefined, { message: 'Provide driverUserId, vehicleId, or both' });
export const cancelBodySchema = z.object({
    reason: z.string().trim().min(5).max(240),
});
// -------- Stops --------
export const createStopBodySchema = z
    .object({
    /** If omitted, service appends to the end (max(sequence) + 1). */
    sequence: z.number().int().min(0).optional(),
    type: loadStopTypeSchema,
    customerAddressId: idSchema.nullable().optional(),
    line1: z.string().trim().min(1).max(160),
    line2: z.string().trim().max(160).nullable().optional(),
    city: z.string().trim().max(80).nullable().optional(),
    postcode: z.string().trim().max(16).nullable().optional(),
    country: isoCountrySchema.optional(),
    lat: latitudeSchema.nullable().optional(),
    lng: longitudeSchema.nullable().optional(),
    what3words: z.string().trim().max(80).nullable().optional(),
    contactName: z.string().trim().max(120).nullable().optional(),
    contactPhoneE164: phoneE164Schema.nullable().optional(),
    windowStartAt: z.string().datetime().nullable().optional(),
    windowEndAt: z.string().datetime().nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
    instructions: z.string().trim().max(2000).nullable().optional(),
})
    .superRefine((v, ctx) => {
    const hasLat = v.lat !== null && v.lat !== undefined;
    const hasLng = v.lng !== null && v.lng !== undefined;
    if (hasLat !== hasLng) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: hasLat ? ['lng'] : ['lat'],
            message: 'lat and lng must be provided together',
        });
    }
});
export const updateStopBodySchema = z
    .object({
    sequence: z.number().int().min(0).optional(),
    type: loadStopTypeSchema.optional(),
    line1: z.string().trim().min(1).max(160).optional(),
    line2: z.string().trim().max(160).nullable().optional(),
    city: z.string().trim().max(80).nullable().optional(),
    postcode: z.string().trim().max(16).nullable().optional(),
    country: isoCountrySchema.optional(),
    lat: latitudeSchema.nullable().optional(),
    lng: longitudeSchema.nullable().optional(),
    what3words: z.string().trim().max(80).nullable().optional(),
    contactName: z.string().trim().max(120).nullable().optional(),
    contactPhoneE164: phoneE164Schema.nullable().optional(),
    windowStartAt: z.string().datetime().nullable().optional(),
    windowEndAt: z.string().datetime().nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
    instructions: z.string().trim().max(2000).nullable().optional(),
})
    .strict();
// -------- Driver stop events (S78/S79, docs/driver-stop-flow.md) --------
// Fresh GPS fix. Optional as a PAIR: no-coords stops accept a bare arrival
// (founder ruling); stops with coords reject bare arrivals in the service.
export const arriveStopBodySchema = z
    .object({
    lat: latitudeSchema.optional(),
    lng: longitudeSchema.optional(),
    idempotencyKey: idempotencyKeySchema.optional(),
})
    .superRefine((v, ctx) => {
    if ((v.lat !== undefined) !== (v.lng !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: v.lat !== undefined ? ['lng'] : ['lat'],
            message: 'lat and lng must be provided together',
        });
    }
});
// Dispatcher escape hatch (walk-test P1-6): advance a stop one rung with a
// required reason; audited as an override. The only legitimate path when
// GPS fails at the gate.
export const advanceStopBodySchema = z.object({
    reason: z.string().trim().min(5).max(300),
    idempotencyKey: idempotencyKeySchema.optional(),
    /**
     * Unit D compare-and-set: the stop status the dispatcher was looking at.
     * When present and stale, the advance 409s instead of silently moving the
     * stop another rung — two impatient taps cannot over-advance.
     */
    expectedStatus: loadStopStatusSchema.optional(),
});
/** Unit D: set-off/complete previously took no body; both now accept a key. */
export const stopEventBodySchema = z.object({
    idempotencyKey: idempotencyKeySchema.optional(),
});
/** S79 Something-wrong path — a driver reports a stop problem. */
export const stopProblemBodySchema = z.object({
    kind: z.enum(['failed', 'skipped']),
    reason: z.string().trim().min(5).max(500),
});
//# sourceMappingURL=load.js.map